/**
 * ChatPanel — right-rail chat container, 380px wide on desktop.
 *
 * HANDOFF_AI_CHAT_FEATURE_V1_REVISED §3, §8 (revised — standalone-only V1).
 *
 * Mounts in MainLayout. Hidden when:
 *   - chat_enabled feature flag is off
 *   - user closes via X / Cmd+/ toggle
 *
 * V1 ships the panel only — V1.5 will add a standalone /chat page using the
 * same store and stream hook.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Clock, PlusCircle, X } from 'react-feather';

import { useChatSessions } from '../../../api/hooks/useChatSessions';
import { useMobile } from '../../../hooks/useMobile';
import { getChatEnabled } from '../lib/chat-access';
import { followUpRegistry } from '../lib/follow-up-registry';
import { useAuthStore } from '../../../stores/authStore';
import { useChatStore } from '../../../stores/chatStore';
import { useAutoScroll } from '../hooks/useAutoScroll';
import { useChatDealHydration } from '../hooks/useChatDealHydration';
import { useChatHistoryActions } from '../hooks/useChatHistoryActions';
import { useChatStream } from '../hooks/useChatStream';
import { useChatTelemetry } from '../hooks/useChatTelemetry';
import { ChatContextualChips } from './ChatContextualChips';
import { ChatEmptyState } from './ChatEmptyState';
import { ChatHistorySkeleton } from './ChatHistorySkeleton';
import { ChatErrorBanner } from './ChatErrorBanner';
import { ChatFollowUpChips } from './ChatFollowUpChips';
import { ChatGapChip } from './ChatGapChip';
import { ChatInput } from './ChatInput';
import { ChatMessage } from './ChatMessage';
import { ChatDealCreationCard } from './ChatDealCreationCard';
import { ChatDealSelectionCard } from './ChatDealSelectionCard';
import { ChatDealSelectedCard } from './ChatDealSelectedCard';
import { ChatTermSheetSelectionCard } from './ChatTermSheetSelectionCard';
import { ChatNavigateChip } from './ChatNavigateChip';
import { ChatStreamingIndicator } from './ChatStreamingIndicator';
import { ChatUploadCard } from './ChatUploadCard';
import { RecentChats } from './RecentChats';

export function ChatPanel() {
  const isMobile = useMobile();
  const user = useAuthStore((s) => s.user);
  // Mirror backend require_chat_enabled: ADMIN/OPS bypass the per-user flag.
  const chatEnabled = getChatEnabled(user);

  const isOpen = useChatStore((s) => s.isOpen);
  const messages = useChatStore((s) => s.messages);
  const isStreaming = useChatStore((s) => s.isStreaming);
  const streamingPhase = useChatStore((s) => s.streamingPhase);
  const gapQuestion = useChatStore((s) => s.gapQuestion);
  const clearGapQuestion = useChatStore((s) => s.clearGapQuestion);
  const pendingNavigation = useChatStore((s) => s.pendingNavigation);
  const uploadPrompt = useChatStore((s) => s.uploadPrompt);
  const inflightUpload = useChatStore((s) => s.inflightUpload);
  const dealCreationPrompt = useChatStore((s) => s.dealCreationPrompt);
  const dealSelectionPrompt = useChatStore((s) => s.dealSelectionPrompt);
  const dealSelectionResolved = useChatStore((s) => s.dealSelectionResolved);
  const termSheetSelectionPrompt = useChatStore(
    (s) => s.termSheetSelectionPrompt,
  );
  const close = useChatStore((s) => s.close);
  const toggle = useChatStore((s) => s.toggle);
  const sessionId = useChatStore((s) => s.sessionId);
  const sessions = useChatStore((s) => s.sessions);
  const sessionsLoaded = useChatStore((s) => s.sessionsLoaded);
  const isLoadingHistory = useChatStore((s) => s.isLoadingHistory);

  const { send, cancel } = useChatStream();
  const { logEvent } = useChatTelemetry();

  // Citations Phase 1 — resolve the latest analyzed term sheet for the
  // active deal so ChatMessage can render the "View term sheet" link.
  useChatDealHydration();
  // Auto-scroll only when the user is already at the bottom — leaves them
  // in place if they've scrolled up to read an earlier turn. Pill below
  // brings them back on demand.
  const { ref: scrollRef, isAtBottom, scrollToBottom, pinMessageToTop } =
    useAutoScroll<HTMLDivElement>([messages, isStreaming]);

  // Claude.ai-style scroll: pin the freshest non-synthetic user message
  // to the top of the viewport so the streaming response has the empty
  // space below. We scan backwards because send() appends the user
  // message AND an empty assistant placeholder in the same tick — by
  // the time this effect runs, the actual last message is the assistant
  // placeholder, not the user msg. Synthetic messages from inline cards
  // (deal creation, upload, etc.) are hidden and must not trigger pin.
  const lastPinnedIdRef = useRef<string | null>(null);
  useEffect(() => {
    let candidateId: string | null = null;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m.role === 'user' && !m.synthetic) {
        candidateId = m.id;
        break;
      }
    }
    if (!candidateId || lastPinnedIdRef.current === candidateId) {return;}
    lastPinnedIdRef.current = candidateId;
    pinMessageToTop(candidateId);
  }, [messages, pinMessageToTop]);
  // V1.1 Item 4 — recent chats history dropdown.
  const [historyOpen, setHistoryOpen] = useState(false);
  // Populate the sessions list when chat is enabled — gated to avoid 403s
  // from /chat/sessions for users without the feature flag.
  useChatSessions(20, chatEnabled);
  const {
    handleNewChat,
    handleSelectSession,
    handleRenameSession,
    handleDeleteSession,
  } = useChatHistoryActions({ cancel });

  const activeSessionTitle = useMemo(() => {
    if (!sessionId) {return null;}
    return sessions.find((s) => s.id === sessionId)?.title ?? null;
  }, [sessionId, sessions]);

  // Cmd+/ (or Ctrl+/) toggles the panel — keyboard shortcut per §8.
  useEffect(() => {
    if (!chatEnabled) {return;}
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        toggle();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [chatEnabled, toggle]);

  const lastAssistantTools = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m.role === 'assistant' && m.toolResults && m.toolResults.length) {
        return m.toolResults.map((t) => t.tool_name);
      }
    }
    return [];
  }, [messages]);

  // True when the most recent assistant turn was pure prose (no tool calls).
  // Used to show contextual onboarding chips instead of tool-based follow-ups.
  const lastAssistantWasPureText = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m.role === 'assistant') {
        return (!m.toolResults || m.toolResults.length === 0) && Boolean(m.content);
      }
    }
    return false;
  }, [messages]);

  // Most recent user message — feeds the ↑ recall in ChatInput.
  const lastUserMessage = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'user' && messages[i].content) {
        return messages[i].content;
      }
    }
    return null;
  }, [messages]);

  // Term sheet count from the most recent get_deal tool result anywhere in
  // the conversation. Used to suppress the compare-versions chip when there
  // is only one term sheet on the deal.
  const lastDealTermSheetCount = useMemo<number | undefined>(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (!m.toolResults) {continue;}
      for (let j = m.toolResults.length - 1; j >= 0; j -= 1) {
        const tr = m.toolResults[j];
        if (tr.tool_name !== 'get_deal') {continue;}
        const r = (tr.result ?? {}) as { term_sheets?: unknown[] };
        if (Array.isArray(r.term_sheets)) {return r.term_sheets.length;}
      }
    }
    return undefined;
  }, [messages]);

  // Telemetry — fire follow_up_shown once per chip-set rendering. Keyed
  // by the assistant turn's messageId so reloading the panel doesn't
  // re-fire for the same chips.
  const lastShownChipsKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (isStreaming || gapQuestion || lastAssistantTools.length === 0 || lastAssistantWasPureText) {return;}
    const lastTool = lastAssistantTools[lastAssistantTools.length - 1];
    const allChips = followUpRegistry[lastTool] ?? [];
    let chips = allChips;
    if (typeof lastDealTermSheetCount === 'number' && lastDealTermSheetCount <= 1) {
      chips = chips.filter((c) => c.id !== 'compare-versions');
    }
    chips = chips.slice(0, 3);
    if (chips.length === 0) {return;}
    const key = `${lastTool}:${chips.map((c) => c.id).join(',')}`;
    if (lastShownChipsKeyRef.current === key) {return;}
    lastShownChipsKeyRef.current = key;
    logEvent('follow_up_shown', {
      tool_name: lastTool,
      chip_count: chips.length,
      chip_ids: chips.map((c) => c.id),
    });
  }, [isStreaming, gapQuestion, lastAssistantTools, lastDealTermSheetCount, logEvent, lastAssistantWasPureText]);

  // ChatPanel is the desktop right-rail. On mobile the dedicated /chat page
  // owns the surface, so suppress the panel entirely.
  if (!chatEnabled || !isOpen || isMobile) {return null;}

  return (
    <aside
      aria-label="Credia AI chat"
      className="fixed right-0 top-14 z-40 flex h-[calc(100vh-3.5rem)] w-full max-w-[380px] flex-col border-l border-border bg-surface-main shadow-lg"
    >
      <header className="relative flex items-center justify-between border-b border-border bg-surface-card px-3 py-2">
        <button
          type="button"
          onClick={() => setHistoryOpen((v) => !v)}
          aria-label="Recent chats"
          title="Recent chats"
          aria-expanded={historyOpen}
          className={
            historyOpen
              ? 'text-orange'
              : 'text-txt-tertiary hover:text-txt-primary'
          }
        >
          <Clock size={16} />
        </button>

        <div className="flex items-center gap-2 truncate">
          <span aria-hidden className="animate-credia-pulse inline-block h-3 w-3 flex-shrink-0 rounded-full bg-orange" />
          <p className="max-w-[160px] truncate text-body-sm font-medium text-txt-primary">
            {activeSessionTitle ?? 'Credia AI'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleNewChat}
            aria-label="New chat"
            title="New chat"
            className="text-txt-tertiary hover:text-txt-primary"
          >
            <PlusCircle size={14} />
          </button>
          <button
            type="button"
            onClick={close}
            aria-label="Close chat"
            className="text-txt-tertiary hover:text-txt-primary"
          >
            <X size={16} />
          </button>
        </div>

        {historyOpen && (
          <div className="custom-scrollbar absolute left-0 right-0 top-full z-50 max-h-64 overflow-y-auto border-b border-border bg-surface-main shadow-md">
            <RecentChats
              sessions={sessions}
              activeSessionId={sessionId}
              isLoading={!sessionsLoaded}
              onSelect={(id) => {
                handleSelectSession(id);
                setHistoryOpen(false);
              }}
              onNew={() => {
                handleNewChat();
                setHistoryOpen(false);
              }}
              onRename={handleRenameSession}
              onDelete={handleDeleteSession}
              condensed
            />
          </div>
        )}
      </header>

      <div className="relative flex-1 overflow-hidden">
        <div ref={scrollRef} className="custom-scrollbar h-full overflow-y-auto">
          {isLoadingHistory ? (
            <ChatHistorySkeleton />
          ) : messages.length === 0 ? (
            <ChatEmptyState onPromptClick={(text) => send({ text })} />
          ) : (
            <>
              {messages.map((m) => (
                <ChatMessage key={m.id} message={m} />
              ))}
              {/* Skeleton only during the 'thinking' phase — vanishes the
                  moment text starts arriving. */}
              {streamingPhase === 'thinking' && <ChatStreamingIndicator />}
              {/* Gap chip takes precedence over follow-up chips: when the
                  agent paused the loop on a missing field, the next user
                  action belongs to that conversation, not a new branch. */}
              {!isStreaming && gapQuestion && (
                <div className="px-4 pb-3">
                  <ChatGapChip
                    field={gapQuestion.field}
                    suggestion={gapQuestion.suggestion}
                    source={gapQuestion.source}
                    blocking={gapQuestion.blocking}
                    prose={gapQuestion.prose}
                    onConfirm={(value) => {
                      clearGapQuestion();
                      send({ text: value });
                    }}
                  />
                </div>
              )}
              {/* Tool-based follow-up chips — only when the last assistant
                  turn used tools. Suppressed when the last turn was pure
                  text (capability / onboarding response), OR when any UI
                  control card (deal picker, TS picker, upload, deal-creation,
                  navigate) is visible — the card owns the next action. */}
              {!isStreaming && !gapQuestion && !lastAssistantWasPureText && lastAssistantTools.length > 0 &&
                !dealSelectionPrompt && !termSheetSelectionPrompt &&
                !uploadPrompt && !inflightUpload && !dealCreationPrompt && !pendingNavigation && (
                <div className="px-4 pb-3">
                  <ChatFollowUpChips
                    toolNames={lastAssistantTools}
                    onPick={(prompt) => send({ text: prompt })}
                    termSheetCount={lastDealTermSheetCount}
                  />
                </div>
              )}
              {/* Contextual onboarding chips — stage-based CTAs rendered when
                  the last assistant turn was pure prose (no tools fired,
                  e.g. a capability response). Suppressed whenever a UI
                  control card (deal picker, TS picker, upload, deal-creation,
                  navigate) is already visible — those cards own the next
                  action and chips would be clutter. */}
              {!isStreaming && !gapQuestion && lastAssistantWasPureText &&
                !dealSelectionPrompt && !termSheetSelectionPrompt &&
                !uploadPrompt && !inflightUpload && !dealCreationPrompt && !pendingNavigation && (
                <div className="px-4 pb-3">
                  <ChatContextualChips
                    onPick={(prompt) => send({ text: prompt })}
                    termSheetCount={lastDealTermSheetCount}
                  />
                </div>
              )}
              {/* Navigate chip — V1.1 Item 6. Rendered when navigate_to SSE
                  arrives. Independent of follow-up chips: the model writes
                  prose AND offers a navigation CTA in the same turn. */}
              {!isStreaming && pendingNavigation && (
                <div className="px-4 pb-3">
                  <ChatNavigateChip />
                </div>
              )}
              {/* Upload card — request_upload. Rendered when an
                  upload_prompt SSE arrives OR an upload is mid-extraction.
                  The card owns the full upload + extraction status visual
                  and fires the synthetic resume message only when the term
                  sheet has been fully extracted; this keeps the chat
                  stream free in the meantime. Not gated on isStreaming —
                  the loading view stays visible while the user chats
                  about other things in parallel. */}
              {(uploadPrompt || inflightUpload) && (
                <div className="px-4 pb-3">
                  <ChatUploadCard />
                </div>
              )}
              {/* Deal-creation card — V1.4 request_deal_creation. Rendered
                  when a deal_creation_prompt SSE arrives. The card collects
                  the four cohort-critical fields with structured input and
                  POSTs /deals directly, then resumes the agent with a
                  synthetic message containing the new deal context. */}
              {!isStreaming && dealCreationPrompt && (
                <div className="px-4 pb-3">
                  <ChatDealCreationCard />
                </div>
              )}
              {/* Selection cards — V1.5 request_deal_selection /
                  request_term_sheet_selection. Rendered when their SSE
                  prompts arrive. Click flow sets dealContext (deal card)
                  and resumes the agent with a synthetic confirmation
                  message. No navigation links anywhere. */}
              {!isStreaming && dealSelectionPrompt && (
                <div className="px-4 pb-3">
                  <ChatDealSelectionCard />
                </div>
              )}
              {/* Persistent compact confirmation of the deal picked above —
                  replaces the picker so the choice stays in view. Not gated on
                  isStreaming so it remains while the agent's reply streams. */}
              {dealSelectionResolved && (
                <div className="px-4 pb-3">
                  <ChatDealSelectedCard deal={dealSelectionResolved} />
                </div>
              )}
              {!isStreaming && termSheetSelectionPrompt && (
                <div className="px-4 pb-3">
                  <ChatTermSheetSelectionCard />
                </div>
              )}
              {/* Persistent error banner with Retry button — sits between
                  the message stream and the input row. */}
              <ChatErrorBanner />
              {/* Bottom scroll headroom — must be at least one viewport
                  tall, otherwise pin-to-top's requested scrollTop exceeds
                  the max and the browser clamps it. The clamped position
                  no longer matches the recorded programmatic target, so
                  the scroll listener reads it as the USER landing near the
                  bottom, re-engages tail-follow, and the next text_delta
                  yanks the pinned message off-screen. min-h-screen keeps
                  the pin target reachable so the math works. */}
              <div aria-hidden="true" className="min-h-screen" />
            </>
          )}
        </div>
        {!isAtBottom && messages.length > 0 && (
          <button
            type="button"
            onClick={scrollToBottom}
            className="absolute bottom-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 border border-border bg-surface-card px-3 py-1 text-caption text-txt-secondary shadow-md hover:text-txt-primary"
          >
            <ChevronDown size={12} />
            Jump to latest
          </button>
        )}
      </div>

      <ChatInput
        isStreaming={isStreaming}
        lastUserMessage={lastUserMessage}
        onSend={(text) => send({ text })}
        onStop={cancel}
      />
      {/* L5 — Non-advisory disclaimer (Phase E pre-launch) */}
      <div className="border-t border-border px-4 py-1.5 text-center text-[11px] text-txt-tertiary">
        Analysis only — not financial advice. Consult your advisor before making decisions.
      </div>
    </aside>
  );
}
